import { Checkbox, SelectChangeEvent } from '@mui/material';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { colors, fontSize, fontWeight, TypographyStyledGlobal } from '../../../theme';
import FormInfoTooltip from '../FormInfoTooltip';
import { FormGroup } from '../formInterfaces';
import { FormLabelStyled, StyledFormControl, MenuItemStyled, SelectStyled, RequiredAsteriskStyled } from '../styled';

const SELECT_ALL_ID = '__select_all__';

const MultiSelectListForm: FC<Partial<FormGroup>> = ({ label, placeholder, value, onChange, choices, isOptional, information }) => {
    const { t } = useTranslation('settings');
    const selectedValues: string[] = Array.isArray(value) ? value : [];

    const realChoices = choices ?? [];

    const realIds = realChoices.map((c) => String(c.id));
    const allSelected = realIds.length > 0 && realIds.every((id) => selectedValues.includes(id));
    const someSelected = !allSelected && realIds.some((id) => selectedValues.includes(id));

    const handleChange = (e: SelectChangeEvent<unknown>) => {
        const ids = e.target.value as string[];

        if (ids.includes(SELECT_ALL_ID)) {
            onChange?.(allSelected ? [] : realIds);

            return;
        }
        onChange?.(ids);
    };

    return (
        <StyledFormControl>
            <FormLabelStyled>
                {label}{information && <FormInfoTooltip information={information} />}{isOptional === false && <RequiredAsteriskStyled>*</RequiredAsteriskStyled>}
            </FormLabelStyled>
            <SelectStyled
                id="multi-select-list"
                labelId="multi-select-list-label"
                onChange={handleChange}
                renderValue={(selected: unknown) => {
                    const ids = selected as string[];
                    if (!ids || ids.length === 0) {
                        return (
                            <TypographyStyledGlobal
                                color={colors.greyPlaceholder}
                                size={fontSize.sm}
                                weight={fontWeight.semiRegular}
                            >
                                {placeholder}
                            </TypographyStyledGlobal>
                        );
                    }

                    return ids
                        .map((id) => realChoices.find((c) => String(c.id) === id)?.label)
                        .filter(Boolean)
                        .join(', ');
                }}
                value={selectedValues}
                displayEmpty
                multiple
            >
                <MenuItemStyled key={SELECT_ALL_ID} value={SELECT_ALL_ID}>
                    <Checkbox checked={allSelected} indeterminate={someSelected} size="small" />
                    {t('form.selectAll')}
                </MenuItemStyled>
                {realChoices.map((choice) => (
                    <MenuItemStyled key={String(choice.id)} value={String(choice.id)}>
                        <Checkbox checked={selectedValues.includes(String(choice.id))} size="small" />
                        {choice.label}
                    </MenuItemStyled>
                ))}
            </SelectStyled>
        </StyledFormControl>
    );
};

export default MultiSelectListForm;
